/**
 * The web editor's image node (#1106): Tiptap's `Image` with a node view that
 * paints `sitescope-attachment://` and message refs from the preview map
 * rather than letting the browser GET them. Moved out of
 * `RichTextEditor.web.tsx` so `webEditorExtensions.ts` can build the
 * production extension list without the whole editor module behind it.
 */
import Image from '@tiptap/extension-image';

import {
  pendingInlineImagePreviewSrc,
  SS_ATTACHMENT_SRC_ATTR,
} from '../../services/pendingInlineImagePreview';
import { isBrowserSafeInlineImageSrc } from '../../utils/richContent/localNetworkImgSrc';
import { displaySrcForInlineImage } from '../richHtmlContentShared';

export const SiteScopeImage = Image.extend({
  parseHTML() {
    return [
      {
        tag: `img[${SS_ATTACHMENT_SRC_ATTR}]`,
        getAttrs: (element: HTMLElement | string) => {
          if (typeof element === 'string') {
            return false;
          }
          const src = element.getAttribute(SS_ATTACHMENT_SRC_ATTR);
          if (!src) {
            return false;
          }
          return {
            src,
            alt: element.getAttribute('alt'),
            title: element.getAttribute('title'),
          };
        },
      },
      {
        tag: 'img[src]:not([src^="data:"])',
      },
    ];
  },
  addNodeView() {
    return ({ node }) => {
      // Wrapper so ProseMirror can stamp node.attrs.src on `dom` without
      // making the browser GET `sitescope-attachment://…`.
      const wrap = document.createElement('span');
      wrap.style.display = 'inline-block';
      wrap.style.maxWidth = '100%';
      const img = document.createElement('img');
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      wrap.appendChild(img);

      let applyGeneration = 0;
      const apply = (src: string, alt: string) => {
        const generation = ++applyGeneration;
        img.alt = alt;
        const cached = pendingInlineImagePreviewSrc(src);
        if (isBrowserSafeInlineImageSrc(cached)) {
          img.src = cached;
          return;
        }
        img.removeAttribute('src');
        void displaySrcForInlineImage(src).then(display => {
          if (generation !== applyGeneration || !display) {
            return;
          }
          if (!isBrowserSafeInlineImageSrc(display)) {
            return;
          }
          img.src = display;
        });
      };

      apply(String(node.attrs.src ?? ''), String(node.attrs.alt ?? ''));
      return {
        dom: wrap,
        update: next => {
          if (next.type.name !== 'image') {
            return false;
          }
          apply(String(next.attrs.src ?? ''), String(next.attrs.alt ?? ''));
          return true;
        },
      };
    };
  },
});
